import { useState } from 'react';
import { Alert, Badge, Button, EmptyState } from '@evogard/ui';
import type { SupervisedExpense } from '@evogard/contract';
import {
  EXPENSE_REQUEST_STAGE_LABELS,
  expenseTotals,
  format,
  formatDueDate,
  type ExpenseRequestStage,
} from '@evogard/domain';
import {
  ExpenseActsModal,
  SupervisorStageBadge,
  beneficiaryOf,
  expenseAmount,
} from '../../../components/expense/SupervisorExpenseBoard.js';
import { useCardExpenses, useClaimExpenseMutations } from './useClaimExpenses.js';

/**
 * A aba Despesas da mesa (26/10/2026): as despesas DESTE card, com o status do
 * supervisor — o atraso já vem mascarado da API. Lança em qualquer fase, por
 * enquanto, e sempre no departamento da aba.
 *
 * No sinistro, desde 03/11/2026, a aba de qualquer departamento lista as do
 * sinistro INTEIRO, cada uma com o selo do departamento que a lançou, e os
 * totais são do sinistro. Aprovar continua sendo do supervisor do departamento
 * da despesa: os atos de cada uma vêm da API.
 *
 * O botão abre a gaveta da mesa (`LaunchExpenseDrawer`), que é de quem monta a
 * mesa: a aba só pede.
 */
export function ExpensesTab({
  stage,
  originId,
  onLaunch,
}: {
  stage: ExpenseRequestStage;
  originId: number;
  onLaunch: () => void;
}) {
  const expenses = useCardExpenses(stage, originId, true);
  const { act } = useClaimExpenseMutations();
  const [openId, setOpenId] = useState<number | null>(null);
  const list = expenses.data?.expenses ?? [];
  const open = list.find((e) => e.id === openId) ?? null;
  const totals = expenses.data ? expenseTotals(list) : null;
  const wholeClaim = stage !== 'perifericos';

  if (expenses.isError) return <Alert tone="erro">{expenses.error.message}</Alert>;
  if (!expenses.data)
    return <p className="text-sm text-texto-suave">Carregando as despesas…</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {totals ? (
          <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <Total label="Lançado" value={format(totals.launched)} />
            <Total label="Pago" value={format(totals.paid)} />
            <Total label="Em aberto" value={format(totals.open)} />
          </dl>
        ) : (
          <p className="text-sm text-texto-suave">
            Um valor não pôde ser lido, e os totais não foram somados.
          </p>
        )}
        {expenses.data.canLaunch && (
          <Button icon="mais" onClick={onLaunch}>
            Lançar despesa
          </Button>
        )}
      </div>
      {list.length === 0 ? (
        <EmptyState icon="dinheiro" title="Nenhuma despesa">
          {wholeClaim
            ? 'Nada foi lançado neste sinistro, em nenhum departamento.'
            : `Nada foi lançado neste card em ${EXPENSE_REQUEST_STAGE_LABELS[stage]}.`}{' '}
          A despesa lançada aqui passa pelo supervisor antes de chegar ao Financeiro.
        </EmptyState>
      ) : (
        <ul className="flex flex-col divide-y divide-borda rounded-borda border border-borda">
          {list.map((e) => (
            <li key={e.id}>
              <ExpenseRow
                expense={e}
                showStage={wholeClaim}
                onOpen={() => setOpenId(e.id)}
              />
            </li>
          ))}
        </ul>
      )}
      {open && (
        <ExpenseActsModal
          expense={open}
          onClose={() => setOpenId(null)}
          onAct={(id, a, reason) => act.mutateAsync({ id, act: a, reason })}
        />
      )}
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-texto-suave">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function ExpenseRow({
  expense,
  showStage,
  onOpen,
}: {
  expense: SupervisedExpense;
  /** O selo do departamento que lançou — no sinistro, onde a aba mostra todos. */
  showStage: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-11 w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 px-3 py-2.5 text-left text-sm hover:bg-superficie-suave"
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-semibold">{beneficiaryOf(expense)}</span>
        <span className="text-xs text-texto-suave tabular-nums">
          Vence em {formatDueDate(expense.dueDate)}
        </span>
      </span>
      <span className="flex flex-wrap items-center gap-2">
        {showStage && expense.stage !== null && (
          <Badge>{EXPENSE_REQUEST_STAGE_LABELS[expense.stage]}</Badge>
        )}
        {expense.alreadyPaid && <Badge tone="sucesso">Já pago</Badge>}
        <SupervisorStageBadge status={expense.status} />
        <span className="font-semibold tabular-nums">
          {expenseAmount(expense.amount)}
        </span>
      </span>
    </button>
  );
}
